import * as React from "react"
import { Link } from "react-router-dom"
import { ArrowRight, CircleHelp, PowerOff, RotateCw, Unplug } from "lucide-react"

import { cn } from "@/lib/utils"
import { toneClass } from "@/lib/status"
import { FEATURE_STATUS, INTEGRATIONS_ADMIN_PATH } from "@/lib/integrations"
import { useAuth } from "@/contexts/AuthContext"
import { useFeature } from "@/hooks/useIntegrationStatus"
import { StatusPill } from "@/components/ui/status-pill"

/*
  NotConfigured — the clear "this needs an outside service that is not set up" state. Use it wherever a
  switched-off feature would otherwise fail (a button that would 503, an empty panel, a disabled sign-in option).

    <NotConfigured feature="email_outbound" />                              // card
    <NotConfigured feature="microsoft_sso" layout="inline" />               // one line, e.g. under a button
    <NotConfigured feature="ai" title="Morning brief needs the AI assistant" text="…" />

    <FeatureGate feature="quickbooks">                                      // children only when ON
      <SyncButton />
    </FeatureGate>
    <FeatureGate feature="ai" fallback={null} loading={<Skeleton className="h-24" />}>…</FeatureGate>

  States (from GET /api/integrations/status via useFeature):
    not_configured → "Not set up yet — ask IT"          (gray "Not set up" pill)
    off            → "Switched off — an admin can turn it on in Admin › Integrations" (amber pill)
    on             → renders nothing
    still loading  → renders nothing (no "not set up" flash before the server answered)
    request failed → neutral "Couldn't check whether X is set up" + Retry (data-status="unknown") — an
                     unreachable server never claims a feature is not set up
  Admins (role Admin / Super Admin — the same rule as the server's switch and checklist, `is_admin_like`) get a
  link to Admin › Integrations; everyone else is told whom to ask.

  Props: feature (backend key: microsoft_sso | passkeys | email_outbound | email_inbound | onedrive |
  quickbooks | ai), layout 'card' (default) | 'inline', title, text (replace the default texts), action
  (extra node, e.g. a "Use username instead" button), canManage (override the Admin check), titleAs ('h3'),
  className, testId ('not-configured').
*/
// Mirrors backend permissions.is_admin_like: only these roles may switch a feature (PUT /api/integrations/{key})
// and see the setup checklist — the `admin` page permission alone is not enough.
const INTEGRATION_ADMIN_ROLES = ["Admin", "Super Admin"]

function useCanManage(canManage) {
  const auth = useAuth()
  if (typeof canManage === "boolean") return canManage
  return INTEGRATION_ADMIN_ROLES.includes(String(auth?.user?.role || "").trim())
}

function NotConfigured({
  feature: featureKey,
  layout = "card",
  title,
  text,
  action,
  canManage,
  titleAs: TitleTag = "h3",
  className,
  testId = "not-configured",
}) {
  const feature = useFeature(featureKey)
  const manage = useCanManage(canManage)
  const off = feature.status === FEATURE_STATUS.OFF
  const label = feature.label || featureKey

  if (feature.status === FEATURE_STATUS.ON) return null
  if (feature.placeholder && feature.loading) return null
  if (feature.placeholder && feature.error) {
    return (
      <StatusUnknown
        label={label}
        layout={layout}
        onRetry={feature.refresh}
        featureKey={featureKey}
        titleAs={TitleTag}
        className={className}
        testId={testId}
      />
    )
  }
  const heading = title || (off ? `${label} is switched off` : `${label} is not set up yet`)
  const help = text ?? (
    off
      ? (manage ? "It is set up on the server — switch it on when you are ready." : "Ask an administrator to switch it on.")
      : (manage ? "IT has to finish the server setup first (see the checklist)." : "Ask IT or your administrator to set it up.")
  )
  const pill = (
    <StatusPill
      size="sm"
      tone={off ? "amber" : "gray"}
      label={off ? "Switched off" : "Not set up"}
    />
  )
  const link = manage ? (
    <Link
      to={feature.admin_url || INTEGRATIONS_ADMIN_PATH}
      className="inline-flex items-center gap-1 whitespace-nowrap text-[13px] font-semibold text-brand-ink hover:underline"
      data-testid={`${testId}-link`}
    >
      Admin › Integrations <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
    </Link>
  ) : null
  const Icon = off ? PowerOff : Unplug

  if (layout === "inline") {
    return (
      <div
        role="note"
        data-testid={testId}
        data-feature={featureKey}
        data-status={feature.status}
        className={cn(
          "flex flex-wrap items-center gap-x-2 gap-y-1 rounded-control border border-dashed border-[color:var(--line-dashed)] bg-surface-2 px-3 py-2 text-[13px] text-ink-2",
          className
        )}
      >
        <Icon className="h-4 w-4 shrink-0 text-ink-3" aria-hidden="true" />
        <span className="min-w-0 flex-1 basis-[calc(100%-24px)] sm:basis-auto">
          <b className="font-semibold text-ink">{heading}.</b> {help}
        </span>
        {link || action ? (
          <span className="ml-6 flex flex-wrap items-center gap-3 sm:ml-0">{link}{action}</span>
        ) : null}
      </div>
    )
  }

  return (
    <section
      data-testid={testId}
      data-feature={featureKey}
      data-status={feature.status}
      aria-label={heading}
      className={cn("flex items-start gap-3.5 rounded-card border border-line bg-surface p-4 shadow-card sm:p-5", className)}
    >
      <span className={cn("ui-icon-tile h-10 w-10 rounded-xl", toneClass(off ? "amber" : "gray"))} aria-hidden="true">
        <Icon className="h-[19px] w-[19px]" />
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex flex-wrap items-center gap-2">
          <TitleTag className="font-heading text-[15px] font-bold text-ink">{heading}</TitleTag>
          {pill}
        </div>
        {feature.description ? <p className="text-[13px] text-ink-2">{feature.description}</p> : null}
        <p className="text-[13px] text-ink-3">{help}</p>
        {link || action ? <div className="mt-1.5 flex flex-wrap items-center gap-3">{link}{action}</div> : null}
      </div>
    </section>
  )
}

/** The status request failed (network, 500): say so neutrally and offer a retry — never "not set up". */
function StatusUnknown({ label, layout, onRetry, featureKey, titleAs: TitleTag, className, testId }) {
  const [retrying, setRetrying] = React.useState(false)
  const retry = async () => {
    setRetrying(true)
    try {
      await onRetry?.()
    } finally {
      setRetrying(false)
    }
  }
  const heading = `Couldn’t check whether ${label} is set up`
  const button = (
    <button
      type="button"
      onClick={retry}
      disabled={retrying}
      className="inline-flex min-h-[32px] items-center gap-1.5 whitespace-nowrap rounded-control px-1 text-[13px] font-semibold text-brand-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
      data-testid={`${testId}-retry`}
    >
      <RotateCw className={cn("h-3.5 w-3.5", retrying && "animate-spin")} aria-hidden="true" />
      {retrying ? "Checking…" : "Retry"}
    </button>
  )
  if (layout === "inline") {
    return (
      <div
        role="note"
        data-testid={testId}
        data-feature={featureKey}
        data-status="unknown"
        className={cn(
          "flex flex-wrap items-center gap-x-2 gap-y-1 rounded-control border border-dashed border-[color:var(--line-dashed)] bg-surface-2 px-3 py-2 text-[13px] text-ink-2",
          className
        )}
      >
        <CircleHelp className="h-4 w-4 shrink-0 text-ink-3" aria-hidden="true" />
        <span className="min-w-0 flex-1"><b className="font-semibold text-ink">{heading}.</b> The server did not answer.</span>
        {button}
      </div>
    )
  }
  return (
    <section
      data-testid={testId}
      data-feature={featureKey}
      data-status="unknown"
      aria-label={heading}
      className={cn("flex items-start gap-3.5 rounded-card border border-line bg-surface p-4 shadow-card sm:p-5", className)}
    >
      <span className={cn("ui-icon-tile h-10 w-10 rounded-xl", toneClass("gray"))} aria-hidden="true">
        <CircleHelp className="h-[19px] w-[19px]" />
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <TitleTag className="font-heading text-[15px] font-bold text-ink">{heading}</TitleTag>
        <p className="text-[13px] text-ink-3">The server did not answer. Check the connection and try again.</p>
        <div className="mt-1">{button}</div>
      </div>
    </section>
  )
}

/** Renders `children` only when `feature` is ON; otherwise `fallback` (default: a NotConfigured card). */
function FeatureGate({ feature, children, fallback, loading = null, notConfiguredProps }) {
  const state = useFeature(feature)
  if (state.enabled) return <>{children}</>
  if (state.loading && state.placeholder) return loading // first load only; a background refresh keeps what is shown
  if (fallback !== undefined) return fallback
  return <NotConfigured feature={feature} {...notConfiguredProps} />
}

export { NotConfigured, FeatureGate }
